/**
 * master-layout — the All Projects board's layout type + URL/preference
 * precedence. Four project-work layouts plus List:
 *   - `headers`   — status columns, project groups (2a, the default).
 *   - `swimlanes` — one lane per project, cells per column (2b).
 *   - `pulse`     — read-only per-project pipeline summary (3b).
 *   - `priority`  — read-only, grouped by priority level (3c).
 *   - `list`      — the existing server-paginated list surface.
 *
 * `MasterLayout` is deliberately its OWN type, not a widened
 * `BoardLayout` (`'board' | 'list'`, `use-list-view-state.ts`) — that type
 * is shared with the per-project board's own Board/List toggle and must
 * stay a two-value union there.
 */

export const MASTER_LAYOUTS = ['headers', 'swimlanes', 'pulse', 'priority', 'list'] as const
export type MasterLayout = (typeof MASTER_LAYOUTS)[number]

export const DEFAULT_MASTER_LAYOUT: MasterLayout = 'headers'

const MASTER_LAYOUT_SET = new Set<string>(MASTER_LAYOUTS)

/**
 * Parse a raw `?layout=` URL value into a `MasterLayout`.
 *   - Any recognised value passes through unchanged — `'list'` included,
 *     since `?layout=list` is the existing deep link into the list
 *     surface and must keep opening it.
 *   - Anything else — a typo, an unknown future value, AND legacy
 *     `'board'` (the master board's only value before this layout
 *     switcher existed, and the per-project `BoardLayout` value) —
 *     returns `undefined` so the caller falls through to the account
 *     preference, then the `'headers'` default.
 *     `'board'` used to map explicitly to `'headers'` here, which meant
 *     an old shared `/?layout=board` link FORCED headers even for an
 *     account whose preference was something else (a URL is supposed to
 *     only ever WIN when it names a real layout) and left `?layout=board`
 *     sitting in the address bar rewritten to `?layout=headers` rather
 *     than stripped. Treating it as just another unrecognised value fixes
 *     both: TanStack Router's own search-normalization strips an
 *     `undefined` field from the URL on load, and the account preference
 *     — not a hardcoded default — decides what actually renders.
 */
export function parseMasterLayout(raw: unknown): MasterLayout | undefined {
  if (typeof raw === 'string' && MASTER_LAYOUT_SET.has(raw)) return raw as MasterLayout
  return undefined
}

/**
 * The RENDERED layout precedence: an explicit, valid `?layout=` URL value
 * wins over the account's stored preference, which wins over the default.
 * Arriving via a URL param never writes back to the preference — only an
 * explicit click does (see `use-master-layout.ts`'s `setLayout`).
 */
export function resolveRenderedLayout(args: {
  urlLayout: MasterLayout | undefined
  preference: MasterLayout
}): MasterLayout {
  return args.urlLayout ?? args.preference
}

/**
 * Compile-time-only assertion that this runtime tuple stays in lockstep
 * with the BE's generated `MasterBoardLayout` enum (`components['schemas']
 * ['MasterBoardLayout']` in `src/api/generated/api.ts`, re-exported as
 * `MasterBoardLayout` from `types-bridge.ts`). This tuple has to be a
 * real array (not a re-export of the generated union) because it drives
 * runtime iteration — `MasterLayoutSwitcher`'s segment order and
 * `MASTER_LAYOUT_SET`'s membership check both need actual values, not
 * just a type. The tuple-wrapped double `extends` below is the standard
 * "exact type equality" idiom (a bare `A extends B` alone would also
 * accept `B` being a WIDER union than `A`) — using it as two independent
 * generic parameters with circular constraints (`A extends B, B extends
 * A`) is rejected by TypeScript as a circular-constraint error, so the
 * check runs entirely inside the conditional type's body instead. Either
 * side drifting from the other (a layout added to one but not the other)
 * is a TYPE ERROR here rather than a silent runtime mismatch discovered
 * only when a value the BE sends fails `isMasterLayout` in
 * `use-master-layout.ts`.
 */
type AssertExact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false
const _masterLayoutMatchesGeneratedSchema: AssertExact<
  MasterLayout,
  import('@/api/types-bridge').MasterBoardLayout
> = true
void _masterLayoutMatchesGeneratedSchema
